// "Waterfall to 98%" — David's audit view of one practice's collections, drawn the way
// he drew it: links to the weekly report PDFs, the date-range control, the floating-bar waterfall from
// the current rate to the 98% line with one bar per block, the blocks table (dollars,
// points, claims, owner, what closes it), the needs-approval queue, the auditability
// table and the analysis box.
//
// Reads GET /api/practices/<id>/waterfall (backend: waterfall_service over the ledger
// review's collections arithmetic). Every bar and every table row's "open" button
// open the same modal (BlockModal) with the claims behind the
// figure. The export buttons download the server's CSV (one shared writer, logged).
// No router, no chart library: local state, fetch, inline SVG.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { downloadExport } from "../reports/exportDownload";
import { cents, count, money, pct0, pct1 } from "../ledgerReview/execCells";
import BalanceChart from "./BalanceChart";
import BlockModal from "./BlockModal";
import WaterfallChart from "./WaterfallChart";
import SendReportDialog from "./SendReportDialog";
import "./Waterfall.css";

const API_URL = process.env.REACT_APP_API_URL || "";

export const PRESETS = [
  ["last_3", "Last 3 counted months"],
  ["last_6", "Last 6"],
  ["ytd", "Year to date"],
];

export function isCompleteDate(value) {
  return /^(19|20)\d{2}-\d{2}-\d{2}$/.test(value || "");
}

// The query the page fetches with; the exports use the same one so the file is the page.
export function rangeParams(preset, from, to) {
  const params = new URLSearchParams();
  if (preset === "custom") {
    if (from) params.set("from", from);
    if (to) params.set("to", to);
  } else {
    params.set("preset", preset);
  }
  return params;
}

async function request(target, options, fallback) {
  const response = await fetch(target, { credentials: "include", ...options });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error((body && body.error) || fallback);
  }
  if (!body || typeof body !== "object") {
    throw new Error("The response could not be read. Try again.");
  }
  return body;
}

function post(url, body) {
  return request(
    url,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
    "The action could not be completed",
  );
}

function stamp(iso) {
  return iso ? iso.slice(0, 10) : "";
}

// The Other bar's drill-down (David, 2026-10-19): what the last bar is made of, by
// category and dollars (the breakdown's "other" category with its claim count, and
// the rounding the bars' tenths of a point left), so the bar is a computed figure
// and never a prose excuse.
export function OtherModal({ walk, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  const other = walk.other || {};
  const components = other.components || [];
  return (
    <div
      className="modal-overlay wf-modal-bg"
      data-testid="waterfall-other-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="wf-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wf-other-title"
      >
        <header className="wf-modal-header">
          <div>
            <h2 id="wf-other-title">
              Other: what the named bars leave to 100%
            </h2>
            <p>
              {money(other.dollars)} · {Number(other.points || 0).toFixed(1)}{" "}
              pts of Expected · the walk ends at{" "}
              {Number(walk.final || 100).toFixed(1)}%
            </p>
          </div>
          <button type="button" className="wf-close" onClick={onClose}>
            Close
          </button>
        </header>
        <div className="wf-modal-body">
          <div className="wf-tbl">
            <table>
              <thead>
                <tr>
                  <th>Component</th>
                  <th className="n">Dollars</th>
                  <th className="n">Points</th>
                  <th className="n">Claims</th>
                </tr>
              </thead>
              <tbody>
                {components.map((c) => (
                  <tr key={c.key} data-testid="waterfall-other-row">
                    <td>{c.label}</td>
                    <td className="n">{money(c.dollars)}</td>
                    <td className="n">
                      {Number(c.points || 0).toFixed(1)} pts
                    </td>
                    <td className="n">{count(c.claims || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function WaterfallPage({
  selectedPractice,
  readOnly = false,
  isAdmin = false,
  isSuperAdmin = false,
  onOpenList,
}) {
  const practiceId = selectedPractice ? selectedPractice.id : null;
  const admin = isAdmin || isSuperAdmin;
  const [preset, setPreset] = useState("last_3");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [applied, setApplied] = useState({
    preset: "last_3",
    from: "",
    to: "",
  });
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // The Send dialog for the Waterfall table (admin only). It opens EMPTY, like the
  // team table's: this table carries the internal block owners and "what closes
  // it" text, so the client's recipients are not one click away.
  const [sendingWaterfall, setSendingWaterfall] = useState(false);
  const [message, setMessage] = useState(null);
  const [modal, setModal] = useState(null); // {kind, key}
  const [busy, setBusy] = useState(false);
  const [editingNote, setEditingNote] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const current = useRef(practiceId);

  const base = `${API_URL}/api/practices/${practiceId}/waterfall`;

  const load = useCallback(
    async (range) => {
      if (!practiceId) return;
      if (
        range.preset === "custom" &&
        !(isCompleteDate(range.from) && isCompleteDate(range.to))
      ) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const body = await request(
          `${base}?${rangeParams(range.preset, range.from, range.to)}`,
          {},
          "Could not load the waterfall",
        );
        if (current.current === practiceId) setPage(body);
      } catch (err) {
        if (current.current === practiceId) setError(err.message);
      } finally {
        if (current.current === practiceId) setLoading(false);
      }
    },
    [practiceId, base],
  );

  useEffect(() => {
    current.current = practiceId;
    setPage(null);
    setModal(null);
    setEditingNote(false);
    load(applied);
  }, [practiceId, applied, load]);

  const choosePreset = (key) => {
    setPreset(key);
    setApplied({ preset: key, from: "", to: "" });
  };

  const rng = page ? page.range : null;
  // The From and To boxes show the range in force: the dates a preset resolved to,
  // or the custom ones. Editing either makes the range custom; it applies as soon
  // as both are complete dates in order.
  const shownFrom = preset === "custom" ? customFrom : rng ? rng.from : "";
  const shownTo = preset === "custom" ? customTo : rng ? rng.to : "";
  const pickDate = (which, value) => {
    const from = which === "from" ? value : shownFrom;
    const to = which === "to" ? value : shownTo;
    setPreset("custom");
    setCustomFrom(from);
    setCustomTo(to);
    if (isCompleteDate(from) && isCompleteDate(to) && from <= to) {
      setApplied({ preset: "custom", from, to });
    }
  };
  const blocks = page ? page.blocks : [];
  const inRate = useMemo(
    () => blocks.filter((b) => b.in_rate !== false),
    [blocks],
  );
  const outsideRate = useMemo(
    () => blocks.filter((b) => b.in_rate === false),
    [blocks],
  );
  const chartBlocks = useMemo(
    () => inRate.filter((b) => Number(b.dollars) > 0),
    [inRate],
  );

  const modalBlock = useMemo(() => {
    if (!page || !modal) return null;
    if (modal.kind === "paid") return page.paid_claims;
    if (modal.kind === "approval") return page.approval_queue;
    if (modal.kind === "in_flight") return page.in_flight_claims || null;
    return blocks.find((b) => b.key === modal.key) || null;
  }, [page, modal, blocks]);

  const openBlock = useCallback((key) => {
    if (key === "today") setModal({ kind: "paid" });
    else if (key === "approval") setModal({ kind: "approval" });
    else if (key === "in_flight") setModal({ kind: "in_flight" });
    else if (key === "other") setModal({ kind: "other" });
    else setModal({ kind: "block", key });
  }, []);
  const closeModal = useCallback(() => setModal(null), []);

  const act = async (fn, success) => {
    setBusy(true);
    setMessage(null);
    try {
      const result = await fn();
      if (success) setMessage({ kind: "success", text: success(result) });
      await load(applied);
    } catch (err) {
      setMessage({
        kind: "error",
        text: err.message || "The action could not be completed.",
      });
    } finally {
      setBusy(false);
    }
  };

  const onWorklist = (block, owner) =>
    act(
      () =>
        post(`${base}/worklist`, {
          claim_ids: block.claim_ids,
          owner: owner || undefined,
          next_action: block.action_text || undefined,
        }),
      (r) =>
        owner
          ? `${count(r.opened + r.assigned)} claims assigned to ${owner} on the worklist (${count(r.opened)} newly opened).`
          : `${count(r.opened)} claims added to the worklist; ${count(r.already_open)} were already there.`,
    );

  const onResolve = (block, resolved) =>
    act(
      () =>
        post(`${base}/blocks/${block.id}/resolve`, {
          resolved,
          from: rng.from,
          to: rng.to,
        }),
      () =>
        resolved
          ? `"${block.name}" marked resolved for ${rng.label}.`
          : `"${block.name}" reopened.`,
    );

  const onDecide = (claimIds, decision) =>
    act(
      () => post(`${base}/writeoff-reviews`, { claim_ids: claimIds, decision }),
      (r) =>
        decision
          ? `${count(r.claims)} write-off${r.claims === 1 ? "" : "s"} ${decision}.`
          : `${count(r.claims)} decision${r.claims === 1 ? "" : "s"} cleared.`,
    );

  const exportCsv = async (blockKey) => {
    setBusy(true);
    setMessage(null);
    try {
      const params = rangeParams(applied.preset, applied.from, applied.to);
      params.set("block", blockKey || "all");
      const name = await downloadExport(
        practiceId,
        "waterfall",
        params,
        "waterfall/export",
      );
      setMessage({ kind: "success", text: `${name} downloaded.` });
    } catch (err) {
      setMessage({
        kind: "error",
        text: err.message || "The export could not be downloaded.",
      });
    } finally {
      setBusy(false);
    }
  };

  // The weekly reports, as the PDFs they are sent as (last complete week). The client
  // one is this practice's with names; the team one is every clinic's, super_admin only.
  const downloadReport = async (which) => {
    setBusy(true);
    setMessage(null);
    try {
      const name =
        which === "team"
          ? await downloadExport(
              null,
              "team-report-pdf",
              null,
              "/api/digest/team",
              "pdf",
            )
          : await downloadExport(
              practiceId,
              "digest-client-pdf",
              new URLSearchParams({ show_names: "1" }),
              "digest/client",
              "pdf",
            );
      setMessage({ kind: "success", text: `${name} downloaded.` });
    } catch (err) {
      setMessage({
        kind: "error",
        text: err.message || "The report could not be downloaded.",
      });
    } finally {
      setBusy(false);
    }
  };

  const importWorklist = async (file, write) => {
    if (!file) return;
    setBusy(true);
    setMessage(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("write", write ? "true" : "false");
      const body = await request(
        `${base}/worklist-import`,
        { method: "POST", body: form },
        "Could not import the worklist",
      );
      const skipped =
        (body.unmatched || []).length + (body.ambiguous || []).length;
      setMessage({
        kind: skipped && !body.matched ? "error" : "success",
        text: `${write ? "Imported" : "Checked"} ${count(body.rows)} rows: ${count(body.matched)} matched${
          skipped
            ? `, ${count(skipped)} not matched (${(body.unmatched || [])
                .concat(body.ambiguous || [])
                .slice(0, 3)
                .map((u) => `line ${u.line}: ${u.why}`)
                .join("; ")}${skipped > 3 ? "; …" : ""})`
            : ""
        }.`,
      });
      if (write) await load(applied);
    } catch (err) {
      setMessage({
        kind: "error",
        text: err.message || "Could not import the worklist.",
      });
    } finally {
      setBusy(false);
    }
  };

  const saveNote = () =>
    act(
      () =>
        request(
          `${base}/note`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ body: noteDraft }),
          },
          "Could not save the analysis",
        ),
      () => "Analysis saved.",
    ).then(() => setEditingNote(false));

  if (!practiceId) {
    return (
      <div className="wf-page">
        <p>Select a practice to see its waterfall.</p>
      </div>
    );
  }

  const tiles = page ? page.tiles : null;
  const rate = tiles ? tiles.current.rate : null;
  const note = page ? page.note : null;
  // Each claim is priced, bucketed and aged by its own kind (2026-10-25): the
  // home-health words while the range holds any 30-day period (a UB-04 claim), and
  // periods as the unit only when every claim in it is one.
  const homeHealth = !!page && !!page.home_health;
  const periodsOnly = !!page && page.claim_mix === "institutional";
  const unit = periodsOnly ? "periods" : "claims";

  return (
    <div className="wf-page" data-testid="waterfall-page">
      <div className="wf-ph">
        <h1>Waterfall to 98%</h1>
        {admin ? (
          <div className="wf-toolbar" data-testid="waterfall-reports">
            <span className="wf-range-label">Reports:</span>
            <button
              type="button"
              className="wf-btn"
              onClick={() => downloadReport("client")}
              disabled={busy}
              data-testid="waterfall-report-client"
            >
              Client (PDF)
            </button>
            {isSuperAdmin ? (
              <button
                type="button"
                className="wf-btn"
                onClick={() => downloadReport("team")}
                disabled={busy}
                data-testid="waterfall-report-team"
              >
                Team (PDF)
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      {error && (
        <div className="wf-message error" role="alert">
          <span>{error}</span>
          <button
            type="button"
            className="message-close"
            aria-label="Dismiss"
            onClick={() => setError(null)}
          >
            ×
          </button>
        </div>
      )}
      {message && (
        <div className={`wf-message ${message.kind}`} role="status">
          <span>{message.text}</span>
          <button
            type="button"
            className="message-close"
            aria-label="Dismiss"
            onClick={() => setMessage(null)}
          >
            ×
          </button>
        </div>
      )}
      {page && page.migration_pending && (
        <div className="wf-pending" data-testid="waterfall-migration-pending">
          The blocks shown are the seeded defaults; saving blocks, decisions and
          the analysis is unavailable until migration {page.migration} has run.
        </div>
      )}

      <div className="wf-range-row" data-testid="waterfall-range">
        <div className="wf-dates">
          <label>
            From
            <input
              type="date"
              aria-label="From"
              value={shownFrom}
              onChange={(e) => pickDate("from", e.target.value)}
            />
          </label>
          <label>
            To
            <input
              type="date"
              aria-label="To"
              value={shownTo}
              onChange={(e) => pickDate("to", e.target.value)}
            />
          </label>
          {loading && <span className="wf-muted">Loading…</span>}
        </div>
        <div className="wf-toolbar">
          {PRESETS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`wf-btn${preset === key ? " selected" : ""}`}
              onClick={() => choosePreset(key)}
              disabled={loading}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {page && (
        <>
          {page.balance ? (
            <section className="wf-card">
              <h3>Collected vs expected</h3>
              <BalanceChart balance={page.balance} />
            </section>
          ) : null}
          <section className="wf-card">
            <h3>Path to 98%</h3>
            <WaterfallChart
              start={rate}
              blocks={chartBlocks}
              target={page.target_rate}
              onSelect={openBlock}
              breakdown={page.if_all_closed || null}
              walk={page.walk || null}
            />
            {page.if_all_closed &&
            page.if_all_closed.categories &&
            page.if_all_closed.categories.some((c) => Number(c.dollars) > 0) ? (
              <div className="wf-tbl" data-testid="waterfall-gap-breakdown">
                <table>
                  <thead>
                    <tr>
                      <th>Remaining gap</th>
                      <th className="n">Dollars</th>
                      <th className="n">Points</th>
                      <th className="n">Claims</th>
                    </tr>
                  </thead>
                  <tbody>
                    {page.if_all_closed.categories
                      .filter((c) => Number(c.dollars) > 0)
                      .map((c) => (
                        <tr key={c.key} data-testid="waterfall-gap-row">
                          <td>
                            {c.label}
                            {c.top_codes && c.top_codes.length ? (
                              <div className="wf-sub" data-testid="waterfall-gap-codes">
                                {c.top_codes
                                  .map(
                                    (t) =>
                                      `${t.code}: ${count(t.claims)} ${
                                        Number(t.claims) === 1
                                          ? "claim"
                                          : "claims"
                                      }, ${money(t.dollars)} open on them`,
                                  )
                                  .join(" · ")}
                              </div>
                            ) : null}
                          </td>
                          <td className="n">{money(c.dollars)}</td>
                          <td className="n">
                            {c.points === null || c.points === undefined
                              ? "—"
                              : `${Number(c.points).toFixed(1)} pts`}
                          </td>
                          <td className="n">{count(c.claims)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </section>

          <section className="wf-card">
            <div className="wf-card-head">
              <h3>Blocks</h3>
              <div className="wf-toolbar">
                <button
                  type="button"
                  className="wf-btn primary"
                  onClick={() => exportCsv("all")}
                  disabled={busy || !page}
                  data-testid="waterfall-export-all"
                >
                  Export all blocks (CSV)
                </button>
                {admin && !readOnly ? (
                  <button
                    type="button"
                    className="wf-btn primary"
                    onClick={() => setSendingWaterfall(true)}
                    disabled={busy || !page}
                    data-testid="waterfall-send"
                  >
                    Send
                  </button>
                ) : null}
                {page && page.worklist_import_enabled && !readOnly ? (
                  <label
                    className="wf-custom"
                    data-testid="waterfall-worklist-import"
                    title={`CSV columns: ${(page.worklist_import_columns || []).join(", ")}`}
                  >
                    Import worklist
                    <input
                      type="file"
                      accept=".csv,text/csv"
                      aria-label="Worklist sheet"
                      disabled={busy}
                      onChange={(e) => {
                        const file = e.target.files && e.target.files[0];
                        e.target.value = "";
                        importWorklist(file, true);
                      }}
                    />
                  </label>
                ) : null}
              </div>
            </div>
            <div className="wf-tbl" data-testid="waterfall-blocks">
              <table>
                <thead>
                  <tr>
                    <th>Block</th>
                    <th className="n">Dollars</th>
                    <th className="n">To 98%</th>
                    <th className="n">Claims</th>
                    <th>Owner</th>
                    <th>What closes it</th>
                  </tr>
                </thead>
                <tbody>
                  {inRate.map((b) => (
                    <tr
                      key={b.key}
                      className={b.resolved ? "resolved" : ""}
                      data-testid="waterfall-block-row"
                    >
                      <td>
                        {b.name}
                        {b.resolved ? (
                          <>
                            {" "}
                            <span className="wf-chip ok">
                              resolved {stamp(b.resolved_at)}
                            </span>
                          </>
                        ) : null}
                      </td>
                      <td className="n">{money(b.dollars)}</td>
                      <td className="n">
                        {Number(b.dollars) > 0 &&
                        b.to_98 !== null &&
                        b.to_98 !== undefined
                          ? `+${Number(b.to_98).toFixed(1)} pts${b.side === "adjustment" ? " (leaves owed)" : b.side === "underpaid" ? " (part joins owed)" : ""}`
                          : "—"}
                      </td>
                      <td className="n">
                        {count(b.claims)}{" "}
                        <button
                          type="button"
                          className="wf-btn sm"
                          onClick={() => openBlock(b.key)}
                          data-testid="waterfall-open-block"
                        >
                          open
                        </button>
                      </td>
                      <td>{b.owner || "—"}</td>
                      <td>{b.action_text || "—"}</td>
                    </tr>
                  ))}
                  {outsideRate.map((b) => (
                    <tr
                      key={b.key}
                      className="muted"
                      data-testid="waterfall-outside-row"
                    >
                      <td>{b.name}</td>
                      <td className="n">{money(b.dollars)}</td>
                      <td className="n">not in the rate</td>
                      <td className="n">
                        {count(b.claims)}{" "}
                        <button
                          type="button"
                          className="wf-btn sm"
                          onClick={() => openBlock(b.key)}
                          data-testid="waterfall-open-outside"
                        >
                          open
                        </button>{" "}
                        <button
                          type="button"
                          className="wf-btn sm"
                          onClick={() => exportCsv(b.key)}
                          disabled={busy}
                          data-testid="waterfall-export-outside"
                        >
                          CSV
                        </button>
                      </td>
                      <td>{b.owner || "—"}</td>
                      <td>{b.action_text || "—"}</td>
                    </tr>
                  ))}
                  {page.service_buckets &&
                  page.service_buckets.in_flight &&
                  Number(page.service_buckets.in_flight.claims) > 0 ? (
                    <tr
                      className={
                        page.service_buckets.in_flight.excluded ? "muted" : ""
                      }
                      data-testid="waterfall-in-flight-row"
                    >
                      <td>
                        In flight: submitted, nothing back from the payer (
                        {page.service_buckets.in_flight.excluded
                          ? "not in owed or the rate"
                          : "in owed at the estimate"}
                        ; charges {money(page.service_buckets.in_flight.billed)}
                        )
                      </td>
                      <td className="n">
                        {money(page.service_buckets.in_flight.dollars)}
                      </td>
                      <td className="n">
                        {page.service_buckets.in_flight.excluded ||
                        page.service_buckets.in_flight.to_98 === null ||
                        page.service_buckets.in_flight.to_98 === undefined
                          ? "not in the rate"
                          : `+${Number(page.service_buckets.in_flight.to_98).toFixed(1)} pts`}
                      </td>
                      <td className="n">
                        {count(page.service_buckets.in_flight.claims)}{" "}
                        <button
                          type="button"
                          className="wf-btn sm"
                          onClick={() => openBlock("in_flight")}
                          data-testid="waterfall-open-in-flight"
                        >
                          open
                        </button>
                      </td>
                      <td>—</td>
                      <td>
                        {page.tiles && page.tiles.in_flight
                          ? `Rate without them: ${pct0(page.tiles.in_flight.rate_excluding)}. `
                          : ""}
                        Chase the ones past 30 days; each re-prices to the
                        payer&apos;s allowed once it rules.
                      </td>
                    </tr>
                  ) : null}
                  {page.service_buckets &&
                  page.service_buckets.lien &&
                  Number(page.service_buckets.lien.claims) > 0 ? (
                    <tr data-testid="waterfall-lien-row">
                      <td>
                        Attorney / WC (settlement-paced): in owed at the
                        trailing paid-to-billed ratio, never at charge; charges{" "}
                        {money(page.service_buckets.lien.billed)}
                      </td>
                      <td className="n">
                        {money(page.service_buckets.lien.dollars)}
                      </td>
                      <td className="n">
                        {page.service_buckets.lien.to_98 === null ||
                        page.service_buckets.lien.to_98 === undefined
                          ? "—"
                          : `+${Number(page.service_buckets.lien.to_98).toFixed(1)} pts`}
                      </td>
                      <td className="n">
                        {count(page.service_buckets.lien.claims)}
                      </td>
                      <td>biller</td>
                      <td>
                        Paid on the settlement or the statute, not a fee
                        schedule; each re-prices to what is paid once it
                        settles.
                      </td>
                    </tr>
                  ) : null}
                  {page.service_buckets &&
                  page.service_buckets.underpaid_schedule &&
                  Number(page.service_buckets.underpaid_schedule.dollars) >
                    0 ? (
                    <tr data-testid="waterfall-oa-row">
                      <td>
                        Underpaid vs the fee schedule (OA: expected above the
                        allowed, beyond the open balance)
                      </td>
                      <td className="n">
                        {money(page.service_buckets.underpaid_schedule.dollars)}
                      </td>
                      <td className="n">
                        {page.service_buckets.underpaid_schedule.to_98 ===
                          null ||
                        page.service_buckets.underpaid_schedule.to_98 ===
                          undefined
                          ? "—"
                          : `+${Number(page.service_buckets.underpaid_schedule.to_98).toFixed(1)} pts`}
                      </td>
                      <td className="n">
                        {count(page.service_buckets.underpaid_schedule.claims)}
                      </td>
                      <td>biller</td>
                      <td>
                        Appeal or reprocess against the schedule; collecting it
                        adds to collected only.
                      </td>
                    </tr>
                  ) : null}
                  {page.service_buckets &&
                  Number(page.service_buckets.unposted_eras.dollars) > 0 ? (
                    <tr className="muted" data-testid="waterfall-outside-row">
                      <td>ERAs received, not posted (counted once posted)</td>
                      <td className="n">
                        {money(page.service_buckets.unposted_eras.dollars)}
                      </td>
                      <td className="n">not in the rate</td>
                      <td className="n">
                        {count(page.service_buckets.unposted_eras.claims)}
                      </td>
                      <td>—</td>
                      <td>Post the waiting ERAs on the Payments page.</td>
                    </tr>
                  ) : null}
                  {!blocks.length ? (
                    <tr>
                      <td colSpan="6" className="wf-muted">
                        No blocks for this practice yet. An admin can add one (a
                        saved filter over payer, reason code, status, age, CPT
                        or posting type).
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </section>

          <div className="wf-card" data-testid="waterfall-approval">
            <h3>Needs approval</h3>
            <div className="wf-tbl">
              <table>
                <thead>
                  <tr>
                    <th>Flag</th>
                    <th className="n">Claims</th>
                    <th className="n">Written off</th>
                    <th>Who</th>
                    <th>What approving does</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      {page.approval_queue.name}
                      {page.approval_queue.pending ? (
                        <>
                          {" "}
                          <span className="wf-chip warn">
                            {count(page.approval_queue.pending)} pending
                          </span>
                        </>
                      ) : null}
                      {page.approval_queue.rejected ? (
                        <>
                          {" "}
                          <span className="wf-chip bad">
                            {count(page.approval_queue.rejected)} rejected
                          </span>
                        </>
                      ) : null}
                    </td>
                    <td className="n">
                      {count(page.approval_queue.claims)}{" "}
                      <button
                        type="button"
                        className="wf-btn sm"
                        onClick={() => openBlock("approval")}
                        data-testid="waterfall-open-approval"
                      >
                        open
                      </button>
                    </td>
                    <td className="n">{money(page.approval_queue.dollars)}</td>
                    <td>{page.approval_queue.owner}</td>
                    <td>{page.approval_queue.action_text}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="wf-two">
            <details className="wf-card wf-audit">
              <summary>Audit details</summary>
              <div className="wf-tbl">
                <table>
                  <thead>
                    <tr>
                      <th>Figure</th>
                      <th>How it is checked</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Collected {money(tiles.current.collected)}</td>
                      <td>
                        {count(page.paid_claims.claims)} paid claims; every
                        dollar is a posted ERA or a posting on one of them.{" "}
                        <button
                          type="button"
                          className="wf-btn sm"
                          onClick={() => openBlock("today")}
                        >
                          open
                        </button>
                      </td>
                    </tr>
                    <tr>
                      <td>Expected {money(tiles.current.owed)}</td>
                      <td>
                        Allowed {money(tiles.current.allowed)}: every claim at
                        its price, the allowed the remittance or report stated
                        where plausible, else the usual allowed for the payer
                        and CPT (an estimate), else the charge (flagged); never
                        less than what was collected. Expected is the larger of
                        that and the fee schedule&apos;s rate where a row
                        exists. Insurance share{" "}
                        {money(tiles.current.insurance_share)}, patient share{" "}
                        {money(tiles.current.patient_share)}.
                      </td>
                    </tr>
                    {page.fee_schedules ? (
                      <tr data-testid="waterfall-fee-schedules">
                        <td>Fee schedules</td>
                        <td>
                          {page.fee_schedules.loaded
                            ? `${count(page.fee_schedules.rows)} rows; loaded for ${count(page.fee_schedules.payers_loaded)} of ${count(page.fee_schedules.payers.length)} payers in the range: ${page.fee_schedules.payers
                                .map(
                                  (p) =>
                                    `${p.payer || "(no payer)"} ${p.loaded ? "yes" : "no"}`,
                                )
                                .join(", ")}`
                            : "none loaded for this practice: Expected = Allowed on every claim (Setup › Fee schedules)"}
                          {page.expected_basis && page.expected_basis.rows
                            ? ` · Expected from: ${Object.entries(
                                page.expected_basis.rows,
                              )
                                .map(([k, v]) => `${k} ${count(v)}`)
                                .join(", ")}`
                            : ""}
                        </td>
                      </tr>
                    ) : null}
                    {page.tiles.counts ? (
                      <tr data-testid="waterfall-counts">
                        <td>
                          {periodsOnly
                            ? "Periods in the range"
                            : "Claims in the range"}
                        </td>
                        <td>
                          {count(page.tiles.counts.claims)} {unit} priced (
                          {count(page.tiles.counts.in_flight)} in flight);{" "}
                          {count(page.tiles.counts.zero_charge)} with a $0
                          charge
                          {page.data_quality.zero_charge &&
                          page.data_quality.zero_charge.by_source &&
                          page.data_quality.zero_charge.by_source.length
                            ? ` (${page.data_quality.zero_charge.by_source
                                .map(
                                  (s) =>
                                    `${s.source}: ${count(s.count)}, ${s.reason}`,
                                )
                                .join("; ")})`
                            : ""}
                          ; {count(page.tiles.counts.no_lines)} with no lines;{" "}
                          {count(page.tiles.counts.total)} records in all.
                        </td>
                      </tr>
                    ) : null}
                    <tr>
                      <td>Blocks</td>
                      <td>
                        Each is a saved claim filter (payer, reason code,
                        status, age, CPT, posting type); a claim's dollars are
                        counted by one block only, in the table's order.
                        {page.blocks_source === "default"
                          ? " Showing the seeded defaults."
                          : ""}
                      </td>
                    </tr>
                    <tr>
                      <td>Data quality</td>
                      <td>
                        {count(page.data_quality.reason_codes_absent)}{" "}
                        write-offs with no reason code;{" "}
                        {count(page.data_quality.adjustments_exceed_charge)}{" "}
                        claims whose adjustments exceed the charge (priced from
                        the remittance);{" "}
                        <span data-testid="waterfall-allowed-corrected-count">
                          {count(page.data_quality.allowed_corrected || 0)}{" "}
                          claims with a corrected allowed
                        </span>{" "}
                        (an implausible ERA allowed replaced by the usual, or a
                        price raised to what was collected);{" "}
                        <span data-testid="waterfall-priced-at-charge-count">
                          {count(page.data_quality.priced_at_charge || 0)}{" "}
                          claims with a line priced at its charge (
                          {cents(
                            page.data_quality.priced_at_charge_dollars || 0,
                          )}
                          )
                        </span>{" "}
                        for want of any usual allowed.{" "}
                        <span data-testid="waterfall-visit-capped-count">
                          Priced per visit:{" "}
                          {count(page.data_quality.visit_capped || 0)} claims (
                          {cents(page.data_quality.visit_capped_dollars || 0)})
                        </span>
                        ;{" "}
                        <span data-testid="waterfall-bundled-lines-count">
                          Bundled lines:{" "}
                          {count(page.data_quality.bundled_lines || 0)}
                        </span>
                        ;{" "}
                        <span data-testid="waterfall-duplicate-lines-count">
                          Duplicate lines:{" "}
                          {count(page.data_quality.duplicate_lines || 0)} (on{" "}
                          {count(page.data_quality.duplicate_claims || 0)}{" "}
                          claims)
                        </span>
                        {homeHealth && page.data_quality.periods_unpriced ? (
                          <span data-testid="waterfall-home-health-quality">
                            {" "}
                            Periods:{" "}
                            {count(
                              page.data_quality.periods_unpriced.count,
                            )}{" "}
                            unpriced (no 835 and no HIPPS rate,{" "}
                            {cents(page.data_quality.periods_unpriced.dollars)}{" "}
                            at the feed&apos;s allowed or the charge);{" "}
                            {count(
                              (page.data_quality.lupa_periods || {}).count || 0,
                            )}{" "}
                            LUPA (
                            {cents(
                              (page.data_quality.lupa_periods || {})
                                .dollars_open || 0,
                            )}{" "}
                            open, their own block);{" "}
                            {count(
                              (page.data_quality.periods_without_hipps || {})
                                .count || 0,
                            )}{" "}
                            without a HIPPS;{" "}
                            {count(
                              (page.data_quality.visit_count_mismatch || {})
                                .count || 0,
                            )}{" "}
                            whose stated visit count disagrees with the visit
                            lines
                            {page.data_quality.periods_priced_by
                              ? ` · priced by: ${Object.entries(
                                  page.data_quality.periods_priced_by,
                                )
                                  .map(([k, v]) => `${k} ${count(v)}`)
                                  .join(", ")}`
                              : ""}
                            .
                          </span>
                        ) : null}
                        {page.data_quality.unpriced_lines ? (
                          <span data-testid="waterfall-unpriced-lines-count">
                            ; Unpriced lines (no payer ruling, worth the usual
                            or the charge):{" "}
                            {count(page.data_quality.unpriced_lines.lines)} (
                            {cents(page.data_quality.unpriced_lines.dollars)}
                            ), of which{" "}
                            {count(
                              page.data_quality.unpriced_lines.at_charge.lines,
                            )}{" "}
                            at the charge (
                            {cents(
                              page.data_quality.unpriced_lines.at_charge
                                .dollars,
                            )}
                            )
                          </span>
                        ) : null}
                        .
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </details>
            <div className="wf-ai" data-testid="waterfall-analysis">
              <div className="who">
                <span>
                  Analysis
                  {note && note.author
                    ? ` · reviewed by ${note.author} ${stamp(note.updated_at)}`
                    : " · not yet reviewed"}
                </span>
                {admin && !readOnly && !editingNote ? (
                  <button
                    type="button"
                    className="wf-btn sm"
                    onClick={() => {
                      setNoteDraft(note ? note.body : "");
                      setEditingNote(true);
                    }}
                  >
                    {note && note.body ? "Edit" : "Write the analysis"}
                  </button>
                ) : null}
              </div>
              {editingNote ? (
                <>
                  <textarea
                    aria-label="Analysis"
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                  />
                  <div className="wf-toolbar" style={{ marginTop: 6 }}>
                    <button
                      type="button"
                      className="wf-btn primary"
                      onClick={saveNote}
                      disabled={busy}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="wf-btn"
                      onClick={() => setEditingNote(false)}
                      disabled={busy}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <p>
                  {note && note.body ? (
                    note.body
                  ) : (
                    <span className="wf-muted">
                      No analysis written for this practice yet.
                    </span>
                  )}
                </p>
              )}
            </div>
          </div>
        </>
      )}

      {sendingWaterfall && page ? (
        <SendReportDialog
          practiceId={practiceId}
          table="waterfall"
          label={rng ? rng.label : ""}
          span={{
            preset: applied.preset === "custom" ? "" : applied.preset,
            from: applied.preset === "custom" ? applied.from : "",
            to: applied.preset === "custom" ? applied.to : "",
          }}
          prefill={[]}
          onClose={() => setSendingWaterfall(false)}
          onSent={(out) => {
            setSendingWaterfall(false);
            setMessage({
              kind: "success",
              text: `${out.subject} sent to ${(out.recipients || []).join(", ")}.`,
            });
          }}
        />
      ) : null}

      {modal && modal.kind === "other" && page && page.walk ? (
        <OtherModal walk={page.walk} onClose={closeModal} />
      ) : null}

      {modalBlock && (
        <BlockModal
          practiceId={practiceId}
          onOpenList={onOpenList}
          block={modalBlock}
          kind={modal.kind}
          rangeLabel={rng ? rng.label : ""}
          readOnly={readOnly}
          isAdmin={admin}
          busy={busy}
          onClose={closeModal}
          onAssign={(b) => onWorklist(b, b.owner)}
          onWorklist={(b) => onWorklist(b, null)}
          onExport={(b) => exportCsv(b.key)}
          onResolve={onResolve}
          onDecide={onDecide}
        />
      )}
    </div>
  );
}
